import { Button, Flex, FormControl, FormHelperText, FormLabel, Icon } from "@chakra-ui/react";
import { t } from "i18next";
import { useMemo, useState } from "react";
import { FaFileAlt } from "react-icons/fa";
import {
  TextDocumentDto,
  useCreateDocumentTemplateMutation,
  useDeleteTextDocumentMutation,
  useListDocumentTemplatesQuery,
  useUpdateTextDocumentMutation,
} from "../../autogen/bff-api";
import { debouncePerKey } from "../../common/documents/debouncePerKey";
import { TextDocumentModal } from "../../common/documents/TextDocumentModal";
import { useApiError } from "../../common/errors/useApiError";
import { TextDocumentTable } from "../../common/input/TipTap/TextDocumentTable";
import { useSub } from "../../common/subscription/useSub";

export const DocumentTemplates = () => {
  const displayer = useApiError();
  const { canEditDocumentTemplates } = useSub();

  const { data, isLoading } = useListDocumentTemplatesQuery();
  const [createDocumentTemplate, { isLoading: isCreatingDocumentTemplate }] = useCreateDocumentTemplateMutation();
  const [updateDocumentTemplate, { isLoading: isUpdatingDocumentTemplate }] = useUpdateTextDocumentMutation();
  const [deleteDocumentTemplate] = useDeleteTextDocumentMutation();

  const [textDocumentToEdit, setTextDocumentToEdit] = useState<TextDocumentDto>();

  const debouncedUpdateTextDocument = useMemo(
    () =>
      debouncePerKey(
        ({
          textDocumentId,
          name,
          text,
          tags,
        }: {
          textDocumentId: string;
          name?: string;
          text?: string;
          tags?: string[];
        }) =>
          updateDocumentTemplate({
            updateTextDocumentRequest: {
              id: textDocumentId,
              name,
              text,
              tags,
            },
          })
            .unwrap()
            .then(() => undefined),
        ({ textDocumentId }) => textDocumentId,
        300,
      ),
    [updateDocumentTemplate],
  );

  return (
    <>
      {textDocumentToEdit && (
        <TextDocumentModal
          textDocument={textDocumentToEdit}
          onClose={() => setTextDocumentToEdit(undefined)}
          onUpdate={({ title, content, tags }: { title?: string; content?: string; tags?: string[] }) => {
            if (!textDocumentToEdit) throw Error("No text document id - could not edit");
            return debouncedUpdateTextDocument({
              textDocumentId: textDocumentToEdit.id,
              name: title,
              text: content,
              tags,
            });
          }}
          isUpdating={isUpdatingDocumentTemplate}
          editable={canEditDocumentTemplates}
        />
      )}
      {canEditDocumentTemplates && (
        <FormControl>
          <FormLabel>{t("Create document template")}</FormLabel>
          <Flex flexDirection="column" alignItems="start">
            <Button
              colorScheme="teal"
              rightIcon={<Icon as={FaFileAlt} />}
              isLoading={isCreatingDocumentTemplate}
              onClick={async () => {
                try {
                  const created = await createDocumentTemplate({
                    createDocumentTemplateRequest: {
                      name: "",
                      text: "",
                      tags: [],
                    },
                  }).unwrap();
                  setTextDocumentToEdit(created);
                } catch (e) {
                  displayer.trigger(e);
                }
              }}
            >
              {t("Create")}
            </Button>
            <FormHelperText>
              {t("Create a new document template from scratch or based on other templates")}
            </FormHelperText>
          </Flex>
        </FormControl>
      )}
      <FormControl pt={canEditDocumentTemplates ? 8 : 0}>
        <FormLabel>{t("Document templates")}</FormLabel>
        <TextDocumentTable
          isLoading={isLoading}
          noDocumentsText={t("No document templates yet") ?? ""}
          documents={data?.documents}
          onClick={setTextDocumentToEdit}
          onDelete={
            canEditDocumentTemplates
              ? async (id: string) => {
                  await deleteDocumentTemplate({ id }).unwrap();
                }
              : undefined
          }
          maxHeight="full"
        />
      </FormControl>
    </>
  );
};
